import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PrintShell from "../../components/UI/PrintShell";
import { extractHtmlParts } from "../../print/composePrintHtml";
import { consumeDocumentsPrintPayload } from "../../print/documentPrintService";

/**
 * Documents bulk / single print preview — same PrintShell chrome as Patient
 * clinical-form print tabs. Each selected form is its own sheet (new page
 * when printing).
 *
 * Payload is read via {@link consumeDocumentsPrintPayload} (localStorage +
 * Strict Mode–safe memory cache) so the tab always loads when opened from Print.
 */
const DocumentsPrintPreviewPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing print session. Close this tab and try again.");
      return;
    }
    const parsed = consumeDocumentsPrintPayload(token);
    if (!parsed) {
      setError(
        "Print session expired or was opened in another window. Close this tab and print again.",
      );
      return;
    }
    setPayload(parsed);
    setError("");
  }, [token]);

  const sheets = useMemo(() => {
    const docs = Array.isArray(payload?.documents) ? payload.documents : [];
    return docs
      .filter((d) => d?.html && String(d.html).trim())
      .map((doc, index) => {
        const parts = extractHtmlParts(doc.html);
        return {
          key: `${doc.title || "doc"}-${index}`,
          title: doc.title || `Document ${index + 1}`,
          styles: parts.styles,
          body: parts.body,
        };
      });
  }, [payload]);

  const mergedStyles = useMemo(
    () => sheets.map((s) => s.styles).filter(Boolean).join("\n"),
    [sheets],
  );

  useEffect(() => {
    if (!mergedStyles) return undefined;
    const el = document.createElement("style");
    el.setAttribute("data-documents-print-styles", "1");
    el.textContent = mergedStyles;
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, [mergedStyles]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-6">
        <div className="max-w-sm rounded-xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-800">
            Print preview could not be loaded
          </p>
          <p className="mt-1 text-xs text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="min-h-screen animate-pulse bg-slate-100 py-10">
        <div className="mx-auto h-[700px] w-full max-w-4xl rounded-xl border border-slate-200 bg-white" />
      </div>
    );
  }

  if (!sheets.length) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-6">
        <div className="max-w-sm rounded-xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-800">
            No forms to print
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Close this tab and select documents again.
          </p>
        </div>
      </div>
    );
  }

  const title =
    payload.title ||
    `Print ${sheets.length} form${sheets.length === 1 ? "" : "s"}`;
  const subtitle = payload.subtitle || "";

  return (
    <PrintShell title={title} subtitle={subtitle} ready>
      <div className="space-y-6 print:space-y-0">
        {sheets.map((sheet, index) => (
          <div
            key={sheet.key}
            className={`mx-auto w-full max-w-4xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm print:max-w-none print:rounded-none print:border-0 print:shadow-none ${
              index < sheets.length - 1 ? "print-sheet-break" : ""
            }`}
          >
            <div
              className="documents-print-sheet px-4 py-5 sm:px-6 print:px-0 print:py-0"
              dangerouslySetInnerHTML={{ __html: sheet.body }}
            />
          </div>
        ))}
      </div>
    </PrintShell>
  );
};

export default DocumentsPrintPreviewPage;
